import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../../components/SEOMeta";
import styles from "../../../styles/Button.module.css";

const PhishingAttack: React.FC = () => {
  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Phishing Attack: Definition, Types, Prevention & Real-World Examples",
    description: "Complete guide to phishing attacks, including types, prevention strategies, and real-world examples. Learn how to identify and protect against phishing attempts.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    datePublished: "2024-01-15",
    dateModified: "2024-01-15",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/attack/phishing",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
    image: "https://cyberannals.com/images/phishing-attack-infographic.jpg",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="Phishing Attack: Definition, Types, Prevention & Real-World Examples"
        description="Complete guide to phishing attacks, including types, prevention strategies, and real-world examples."
        keywords="phishing, phishing attack, email phishing, spear phishing, whaling, vishing, smishing, social engineering, cyber security, fraud prevention, cybersecurity threats, online security"
        canonical="https://cyberannals.com/blog/attack/phishing"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            Phishing Attack: The Ultimate Guide to Understanding and Preventing Digital Deception
          </h1>
          <div className="flex items-center text-slate-600 dark:text-slate-400 mb-6">
            <span className="mr-4">Updated: January 15, 2024</span>
            <span>•</span>
            <span className="mx-4">Read time: 8 minutes</span>
          </div>
        </header>

        {/* Hero Image */}
        <div className="mb-10 rounded-xl overflow-hidden shadow-lg">
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 h-64 flex items-center justify-center">
            <div className="text-center text-white p-6">
              <h2 className="text-2xl font-bold mb-2">Phishing Attack Infographic</h2>
              <p className="opacity-90">Visual representation of phishing attack vectors and prevention methods</p>
            </div>
          </div>
        </div>

        <div className="space-y-8 text-slate-700 dark:text-slate-300 leading-relaxed">
          <section>
            <p className="text-lg font-semibold mb-4">
              Phishing represents one of the most pervasive and dangerous cybersecurity threats facing individuals and organizations today. This comprehensive guide explores the intricate world of phishing attacks, from basic definitions to advanced prevention strategies, complete with real-world case studies and actionable defense mechanisms.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg my-6">
              <h3 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-3">Key Statistics</h3>
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <li className="bg-white dark:bg-slate-800 p-4 rounded shadow">
                  <div className="text-2xl font-bold text-red-600">36%</div>
                  <div className="text-sm">of data breaches involve phishing</div>
                </li>
                <li className="bg-white dark:bg-slate-800 p-4 rounded shadow">
                  <div className="text-2xl font-bold text-red-600">83%</div>
                  <div className="text-sm">of organizations experienced phishing attacks in 2023</div>
                </li>
                <li className="bg-white dark:bg-slate-800 p-4 rounded shadow">
                  <div className="text-2xl font-bold text-red-600">$4.35M</div>
                  <div className="text-sm">average cost of phishing attacks</div>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Understanding Phishing: The Digital Deception
            </h2>
            <p className="mb-4">
              Phishing is a sophisticated form of social engineering that manipulates human psychology rather than exploiting technological vulnerabilities. Attackers craft deceptive communications that appear legitimate, tricking victims into voluntarily surrendering sensitive information or granting unauthorized access.
            </p>
            
            <div className="flex flex-col md:flex-row gap-6 my-8">
              <div className="md:w-2/3">
                <h4 className="text-xl font-semibold mb-3">The Evolution of Phishing</h4>
                <p>
                  The term "phishing" originated in the mid-1990s, drawing parallels with "fishing" as attackers "fish" for credentials. Early phishing attacks targeted AOL users, but the technique has evolved dramatically. Today's phishing campaigns employ advanced psychological manipulation, personalized content, and exploit current events to increase success rates.
                </p>
              </div>
              <div className="md:w-1/3 bg-slate-100 dark:bg-slate-800 rounded-lg p-4 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">📧</div>
                  <p className="text-sm font-medium">Evolution Timeline</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">1990s to Present</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Comprehensive Guide to Phishing Attack Types
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center mb-3">
                  <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg mr-3">
                    <span className="text-red-600 dark:text-red-400">📧</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Email Phishing</h3>
                </div>
                <p className="mb-3">Mass-distributed emails targeting thousands of recipients with generic content. These often mimic well-known brands like Microsoft, Google, or financial institutions.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Characteristics:</strong> Poor grammar, urgent language, suspicious sender addresses
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center mb-3">
                  <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg mr-3">
                    <span className="text-orange-600 dark:text-orange-400">🎯</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Spear Phishing</h3>
                </div>
                <p className="mb-3">Highly targeted attacks against specific individuals or organizations. Attackers research victims extensively to create personalized, convincing messages.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Success Rate:</strong> 70% higher than generic phishing
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center mb-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg mr-3">
                    <span className="text-purple-600 dark:text-purple-400">🐋</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Whaling</h3>
                </div>
                <p className="mb-3">Targets high-profile executives (CEOs, CFOs, etc.) with sophisticated social engineering. Often involves legal summons, tax issues, or executive matters.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Average Loss:</strong> $1.6 million per incident
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center mb-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3">
                    <span className="text-blue-600 dark:text-blue-400">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Smishing & Vishing</h3>
                </div>
                <p className="mb-3">Smishing uses SMS messages while vishing employs voice calls. Both exploit trust in telecommunications and often pretend to be from banks, government agencies, or tech support.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Growth:</strong> 328% increase in smishing attacks since 2020
                </div>
              </div>
            </div>

            {/* Infographic Placeholder */}
            <div className="my-10 p-6 bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-xl">
              <h3 className="text-xl font-bold text-center mb-4">Phishing Attack Distribution</h3>
              <div className="h-48 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="text-slate-600 dark:text-slate-400">Interactive infographic showing phishing attack types by frequency</p>
                  <p className="text-sm mt-2">(Email: 65%, Spear: 15%, Smishing: 10%, Vishing: 7%, Other: 3%)</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              The Phishing Attack Lifecycle
            </h2>
            
            <div className="relative my-8">
              {/* Timeline visualization */}
              <div className="flex flex-col md:flex-row justify-between items-center mb-10">
                {['Reconnaissance', 'Weaponization', 'Delivery', 'Exploitation', 'Installation', 'Actions'].map((step, index) => (
                  <div key={step} className="flex flex-col items-center mb-4 md:mb-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${index === 0 ? 'bg-green-500' : 'bg-blue-500'} text-white font-bold mb-2`}>
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-center">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 p-6 rounded-lg my-6">
              <h4 className="text-xl font-bold text-amber-800 dark:text-amber-300 mb-3">Psychological Triggers Used</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3">
                  <div className="text-2xl mb-1">⚠️</div>
                  <div className="font-medium">Urgency</div>
                </div>
                <div className="text-center p-3">
                  <div className="text-2xl mb-1">🎁</div>
                  <div className="font-medium">Reward</div>
                </div>
                <div className="text-center p-3">
                  <div className="text-2xl mb-1">👑</div>
                  <div className="font-medium">Authority</div>
                </div>
                <div className="text-center p-3">
                  <div className="text-2xl mb-1">🤝</div>
                  <div className="font-medium">Trust</div>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Notable Real-World Phishing Incidents
            </h2>
            
            <div className="space-y-8">
              <div className="border-l-4 border-red-500 pl-4 py-2">
                <h4 className="text-xl font-bold mb-2">Google Docs Phishing Campaign (2017)</h4>
                <p className="mb-2">This sophisticated attack bypassed traditional email filters by using OAuth tokens. Attackers sent emails appearing to share Google Docs, which redirected users to a legitimate Google login page before harvesting credentials.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Impact:</strong> 1+ million users affected within hours
                </div>
              </div>

              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h4 className="text-xl font-bold mb-2">Target Data Breach (2013)</h4>
                <p className="mb-2">The massive breach began with a phishing email to Target's HVAC vendor. Stolen credentials provided initial network access, leading to one of history's largest retail data breaches.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Financial Impact:</strong> $292 million total costs
                </div>
              </div>

              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h4 className="text-xl font-bold mb-2">Ubiquiti Networks (2015)</h4>
                <p className="mb-2">Employees received fake emails appearing to be from executives, resulting in unauthorized wire transfers of $46.7 million to overseas accounts.</p>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Recovery:</strong> Only $8.1 million recovered
                </div>
              </div>
            </div>

            {/* Case Study Comparison Table */}
            <div className="overflow-x-auto my-10">
              <table className="min-w-full bg-white dark:bg-slate-800 rounded-lg shadow">
                <thead className="bg-slate-100 dark:bg-slate-700">
                  <tr>
                    <th className="py-3 px-4 text-left">Incident</th>
                    <th className="py-3 px-4 text-left">Year</th>
                    <th className="py-3 px-4 text-left">Technique</th>
                    <th className="py-3 px-4 text-left">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <td className="py-3 px-4">Facebook & Google</td>
                    <td className="py-3 px-4">2013-2015</td>
                    <td className="py-3 px-4">Fake Invoices</td>
                    <td className="py-3 px-4">$100M+ stolen</td>
                  </tr>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <td className="py-3 px-4">U.S. Department of Labor</td>
                    <td className="py-3 px-4">2020</td>
                    <td className="py-3 px-4">COVID-19 Relief Fraud</td>
                    <td className="py-3 px-4">$45.6M stolen</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Multiple U.S. Universities</td>
                    <td className="py-3 px-4">2019</td>
                    <td className="py-3 px-4">W-2 Phishing</td>
                    <td className="py-3 px-4">600,000+ records</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Comprehensive Prevention Framework
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
              <div>
                <h4 className="text-xl font-bold mb-4 text-green-700 dark:text-green-400">Technical Controls</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong>Email Filtering:</strong> Implement AI-powered email security solutions with sandboxing capabilities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong>DMARC Implementation:</strong> Deploy SPF, DKIM, and DMARC protocols to prevent email spoofing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong>Multi-Factor Authentication:</strong> Require MFA for all privileged accounts and remote access</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span><strong>Web Filtering:</strong> Block known malicious domains and newly registered domains</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-400">Human Controls</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span><strong>Security Awareness Training:</strong> Conduct quarterly phishing simulations and training sessions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span><strong>Reporting Culture:</strong> Establish easy reporting mechanisms for suspicious emails</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span><strong>Verification Protocols:</strong> Implement call-back procedures for financial transactions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span><strong>Least Privilege Access:</strong> Limit user permissions based on job requirements</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Checklist Infographic */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-slate-800 dark:to-slate-900 p-6 rounded-xl my-8">
              <h4 className="text-xl font-bold text-center mb-6">Phishing Detection Checklist</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: '🔍', text: 'Check sender address' },
                  { icon: '🔗', text: 'Hover over links' },
                  { icon: '📎', text: 'Verify attachments' },
                  { icon: '⏰', text: 'Beware urgency' },
                  { icon: '💬', text: 'Grammar check' },
                  { icon: '🏢', text: 'Contact organization' },
                  { icon: '🔒', text: 'Check HTTPS' },
                  { icon: '📞', text: 'Call to verify' }
                ].map((item, index) => (
                  <div key={index} className="bg-white dark:bg-slate-800 p-4 rounded-lg text-center shadow">
                    <div className="text-2xl mb-2">{item.icon}</div>
                    <div className="text-sm font-medium">{item.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Advanced Detection Technologies
            </h2>
            
            <div className="my-8">
              <div className="mb-6">
                <h4 className="text-xl font-bold mb-3">Machine Learning & AI Solutions</h4>
                <p className="mb-3">
                  Modern phishing detection employs sophisticated AI algorithms that analyze email headers, content patterns, sender behavior, and user interaction patterns. These systems can identify sophisticated attacks that bypass traditional signature-based detection.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-lg">
                  <strong>Key Capabilities:</strong> Natural language processing, behavioral analytics, anomaly detection, predictive threat intelligence
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-xl font-bold mb-3">Threat Intelligence Integration</h4>
                <p>
                  Real-time threat intelligence feeds provide immediate identification of newly registered phishing domains, known malicious IP addresses, and emerging attack patterns. Integration with SIEM systems enables automated response workflows.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Response and Recovery Protocol
            </h2>
            
            <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl my-6">
              <h4 className="text-xl font-bold text-red-800 dark:text-red-300 mb-4">Incident Response Steps</h4>
              <ol className="space-y-3 list-decimal pl-5">
                <li><strong>Immediate Isolation:</strong> Disconnect affected systems from network</li>
                <li><strong>Credential Reset:</strong> Immediately reset all potentially compromised credentials</li>
                <li><strong>Forensic Analysis:</strong> Preserve evidence and determine attack vector</li>
                <li><strong>Communication Plan:</strong> Notify affected parties per regulatory requirements</li>
                <li><strong>Remediation:</strong> Patch vulnerabilities and strengthen controls</li>
                <li><strong>Post-Incident Review:</strong> Analyze lessons learned and update policies</li>
              </ol>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 pb-3 border-b border-slate-200 dark:border-slate-700">
              Future Trends and Emerging Threats
            </h2>
            
            <div className="my-8">
              <div className="mb-6 p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                <h4 className="text-lg font-bold mb-2">AI-Powered Phishing</h4>
                <p>Attackers increasingly use AI to generate personalized content, mimic writing styles, and create convincing deepfake audio/video for vishing attacks.</p>
              </div>
              
              <div className="mb-6 p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                <h4 className="text-lg font-bold mb-2">QR Code Phishing (Quishing)</h4>
                <p>Attackers embed malicious QR codes in emails that bypass traditional URL scanning, directing users to phishing sites when scanned with mobile devices.</p>
              </div>
              
              <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                <h4 className="text-lg font-bold mb-2">Supply Chain Phishing</h4>
                <p>Targeting software vendors and service providers to compromise legitimate updates and distribution channels.</p>
              </div>
            </div>
          </section>

          <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-8 rounded-xl my-12">
            <h3 className="text-2xl font-bold mb-4">Key Takeaways</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                <span>Phishing remains the #1 attack vector for data breaches worldwide</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                <span>Human psychology is the primary target, not technological vulnerabilities</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                <span>Combined technical controls and user education provides 99% protection</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                <span>Regular phishing simulations reduce click rates by up to 85%</span>
              </li>
            </ul>
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          {/* Rest of the footer remains the same as your original code */}
          <div className="flex flex-wrap gap-4 mb-6">
            <Link
              to="/blog"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/blog/every-type-of-cyber-attack"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              ← Back to All Cyber Attacks
            </Link>
            <Link
              to="/toolkit"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              Security Tools →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Popular Topics:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/what-is-cybersecurity" className={styles.link}>What is Cybersecurity?</Link></li>
                <li><Link to="/blog/what-is-firewall" className={styles.link}>What is a Firewall?</Link></li>
                <li><Link to="/blog/what-is-vpn" className={styles.link}>What is a VPN?</Link></li>
                <li><Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI Driven Cyber Attacks</Link></li>
                <li><Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Cyber Attack Types:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/common-cyber-attacks" className={styles.link}>Common Cyber Attacks</Link></li>
                <li><Link to="/blog/most-popular-cyber-attacks" className={styles.link}>Most Popular Cyber Attacks</Link></li>
                <li><Link to="/blog/top-10-cyber-attacks" className={styles.link}>Top 10 Cyber Attacks</Link></li>
                <li><Link to="/blog/worst-cyber-attacks" className={styles.link}>Worst Cyber Attacks</Link></li>
                <li><Link to="/blog/every-type-of-cyber-attack" className={styles.link}>Every Type of Cyber Attack</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Related Cyber Attacks:
              </h3>
              <div className="space-y-2">
                <div><Link to="/event/log4j-log4shell-2021" className={styles.link}>Log4j Log4Shell (2021)</Link></div>
                <div><Link to="/event/heartbleed" className={styles.link}>Heartbleed (2014)</Link></div>
                <div><Link to="/event/stuxnet" className={styles.link}>Stuxnet (2010)</Link></div>
                <div><Link to="/event/operation-aurora" className={styles.link}>Operation Aurora (2009)</Link></div>
                <div><Link to="/event/colonial-pipeline-ransomware" className={styles.link}>Colonial Pipeline Attack (2021)</Link></div>
                <div><Link to="/event/equifax-data-breach-2017" className={styles.link}>Equifax Data Breach (2017)</Link></div>
                <div><Link to="/event/bangladesh-bank-heist" className={styles.link}>Bangladesh Bank Heist (2016)</Link></div>
                <div><Link to="/event/anthem-breach" className={styles.link}>Anthem Breach (2015)</Link></div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4">
              <Link to="/sitemap.xml" className={styles.link}>Sitemap</Link>
              <Link to="/privacy-policy" className={styles.link}>Privacy Policy</Link>
              <Link to="/terms-of-service" className={styles.link}>Terms of Service</Link>
              <Link to="/contact" className={styles.link}>Contact Us</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default PhishingAttack;